import type { Appearance } from "../types";
import data from "./catalog.json";

/**
 * Catalogo do customizador, gerado por tools/genassets/avatar.mjs (make assets).
 * O servidor valida contra uma copia identica, entao qualquer parte listada
 * aqui e aceita la.
 */
export type Rgb = [number, number, number];
/** Quatro tons por cor: luz, base, sombra, contorno. */
export type Ramp = [Rgb, Rgb, Rgb, Rgb];

export interface ColorOption {
  hex: string;
  ramp: Ramp;
}

export interface PartOption {
  id: string;
  label: string;
  /** Peca de cima que usa a segunda cor (topColor2). */
  twoTone?: boolean;
  /** Acessorio de cabeca que esconde o cabelo acima da aba (arquivo `<peca>_cut.png`). */
  cut?: boolean;
}

export type PartKind = "hair" | "facialHair" | "top" | "bottom" | "shoes" | "headwear" | "glasses" | "earrings";

/** Camada do spritesheet: `file` tem "{id}" no lugar da peca escolhida no campo `field`. */
export interface LayerDef {
  name: string;
  field: PartKind | null;
  file: string;
  /** Campo cuja peca recorta esta camada (o chapeu recorta o cabelo). */
  cutBy: PartKind | null;
}

/**
 * Gesto do botao de gestos. Com `frames`, a pose em loop a `fps` (de frente);
 * `hop` = os dois quadros sao agachar/no ar e o Avatar faz o pulo; `emote` =
 * sem pose, so o emoji flutuando acima da cabeca.
 */
export interface GestureDef {
  id: string;
  label: string;
  icon: string;
  frames?: number[];
  fps?: number;
  hop?: boolean;
  emote?: string;
}

/** Campos acrescentados depois da primeira versao do customizador. */
type Extras = Pick<Appearance, "topColor2" | "facialHair" | "headwear" | "headwearColor" | "glasses" | "earrings">;

export const CATALOG = data as unknown as {
  frame: { width: number; height: number; cols: number; rows: number };
  /** Canais de cor dos marcadores (pele, cabelo, cima, baixo, calcado, cima 2, acessorio). */
  channels: number;
  layers: LayerDef[];
  /** Id da opcao "sem peca" das categorias opcionais. */
  none: string;
  parts: Record<PartKind, PartOption[]>;
  colors: { skin: ColorOption[]; hair: ColorOption[]; cloth: ColorOption[] };
  defaults: Extras;
  presets: Appearance[];
  gestures: GestureDef[];
};

export const GESTURES = CATALOG.gestures;

export function gestureById(id: string | undefined): GestureDef | undefined {
  return id ? GESTURES.find((g) => g.id === id) : undefined;
}

/** Completa aparencias antigas (sem acessorios) com os valores padrao. */
export function withDefaults(a: Partial<Appearance> & Omit<Appearance, keyof Extras>): Appearance {
  return { ...CATALOG.defaults, ...a };
}

/** Aparencia equivalente a uma skin antiga (contas que nunca abriram o customizador). */
export function presetFor(skin: number): Appearance {
  const presets = CATALOG.presets;
  return withDefaults(presets[((skin % presets.length) + presets.length) % presets.length]);
}

export function randomAppearance(): Appearance {
  const pick = <T>(list: T[]) => list[Math.floor(Math.random() * list.length)];
  const color = (list: ColorOption[]) => Math.floor(Math.random() * list.length);
  // acessorios aparecem de vez em quando, nao em todo sorteio
  const maybe = (kind: PartKind, chance: number) =>
    Math.random() < chance ? pick(CATALOG.parts[kind].filter((p) => p.id !== CATALOG.none)).id : CATALOG.none;
  return {
    skinTone: color(CATALOG.colors.skin),
    hair: pick(CATALOG.parts.hair).id,
    hairColor: color(CATALOG.colors.hair),
    top: pick(CATALOG.parts.top).id,
    topColor: color(CATALOG.colors.cloth),
    bottom: pick(CATALOG.parts.bottom).id,
    bottomColor: color(CATALOG.colors.cloth),
    shoes: pick(CATALOG.parts.shoes).id,
    shoesColor: color(CATALOG.colors.cloth),
    topColor2: color(CATALOG.colors.cloth),
    facialHair: maybe("facialHair", 0.2),
    headwear: maybe("headwear", 0.3),
    headwearColor: color(CATALOG.colors.cloth),
    glasses: maybe("glasses", 0.25),
    earrings: maybe("earrings", 0.3),
  };
}

/** Mesma codificacao do servidor (model.Appearance.Code): chave de cache. */
export function appearanceCode(appearance: Appearance): string {
  const a = withDefaults(appearance);
  return [
    a.skinTone, a.hair, a.hairColor, a.top, a.topColor, a.bottom, a.bottomColor, a.shoes, a.shoesColor,
    a.topColor2, a.facialHair, a.headwear, a.headwearColor, a.glasses, a.earrings,
  ].join(".");
}

export function sameAppearance(a: Appearance, b: Appearance): boolean {
  return appearanceCode(a) === appearanceCode(b);
}
